import { pinduoduoCostParametersOn, pinduoduoStoreCostOn } from './pinduoduoProfit'
import type { PddAfterSalesDailyRecord, PddAfterSalesDailySkuRecord } from './pinduoduoAfterSales'
import type { CompanyProductOverrideRecord, InternalProductInfoRecord, StoreCostProfile } from './types'

export interface PinduoduoSecondReturnLossEstimate {
  secondReturnOrderCount: number
  secondReturnItemCount: number
  coveredOrderCount: number
  coveredDamageItemCount: number
  roundTripFreightCost: number
  packagingLoss: number
  expectedDamageCost: number
  totalLoss: number
  complete: boolean
  damageRate: number | null
  freightMultiplier: number | null
  missingStoreCostDates: string[]
  missingProductCostVariantIds: string[]
}

export interface PinduoduoAllocatedSecondReturnLossEstimate {
  involvedOrderCount: number
  secondReturnItemCount: number
  coveredDamageItemCount: number
  fulfillmentAllocationRate: number
  allocatedRoundTripFreightCost: number
  allocatedPackagingLoss: number
  expectedDamageCost: number
  totalLoss: number
  complete: boolean
}

export function allocatePinduoduoSecondReturnLossToScope(
  storeLoss: PinduoduoSecondReturnLossEstimate,
  productDirectLoss: PinduoduoSecondReturnLossEstimate,
): PinduoduoAllocatedSecondReturnLossEstimate {
  const fulfillmentAllocationRate = storeLoss.secondReturnItemCount > 0
    ? productDirectLoss.secondReturnItemCount / storeLoss.secondReturnItemCount
    : 0
  const allocatedRoundTripFreightCost = storeLoss.roundTripFreightCost * fulfillmentAllocationRate
  const allocatedPackagingLoss = storeLoss.packagingLoss * fulfillmentAllocationRate
  return {
    involvedOrderCount: productDirectLoss.secondReturnOrderCount,
    secondReturnItemCount: productDirectLoss.secondReturnItemCount,
    coveredDamageItemCount: productDirectLoss.coveredDamageItemCount,
    fulfillmentAllocationRate,
    allocatedRoundTripFreightCost,
    allocatedPackagingLoss,
    expectedDamageCost: productDirectLoss.expectedDamageCost,
    totalLoss: allocatedRoundTripFreightCost + allocatedPackagingLoss + productDirectLoss.expectedDamageCost,
    complete: storeLoss.freightMultiplier !== null
      && storeLoss.coveredOrderCount === storeLoss.secondReturnOrderCount
      && productDirectLoss.damageRate !== null
      && productDirectLoss.coveredDamageItemCount === productDirectLoss.secondReturnItemCount,
  }
}

export function estimatePinduoduoSecondReturnLoss(
  dailyRows: PddAfterSalesDailyRecord[],
  dailySkuRows: PddAfterSalesDailySkuRecord[],
  skuCodeByVariantId: Map<string, string>,
  profile: StoreCostProfile | undefined,
  companyRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  companyOverridesByCode: Map<string, CompanyProductOverrideRecord>,
): PinduoduoSecondReturnLossEstimate {
  const policy = profile?.afterSalesCostPolicy
  let coveredOrderCount = 0
  let coveredDamageItemCount = 0
  let roundTripFreightCost = 0
  let packagingLoss = 0
  let expectedDamageCost = 0
  const missingStoreCostDates = new Set<string>()
  const missingProductCostVariantIds = new Set<string>()
  const inPolicy = (date: string) => Boolean(policy && date >= policy.effectiveDate)
  const eligibleDaily = dailyRows.filter((row) => inPolicy(row.date))
  const eligibleSku = dailySkuRows.filter((row) => inPolicy(row.date))
  const secondReturnOrderCount = eligibleDaily.reduce((sum, row) => sum + row.secondReturnOrderCount, 0)
  const secondReturnItemCount = eligibleSku.reduce((sum, row) => sum + row.shippedCount, 0)

  if (policy) {
    for (const row of eligibleDaily) {
      if (!row.secondReturnOrderCount) continue
      const storeCost = pinduoduoStoreCostOn(profile, row.date)
      if (!storeCost) {
        missingStoreCostDates.add(row.date)
        continue
      }
      roundTripFreightCost += (storeCost.shippingFee ?? 0)
        * policy.secondReturnFreightMultiplier
        * row.secondReturnOrderCount
      if (policy.secondReturnPackagingLossUsesStorePackagingCost) {
        packagingLoss += (storeCost.packagingCost ?? 0) * row.secondReturnOrderCount
      }
      coveredOrderCount += row.secondReturnOrderCount
    }

    for (const row of eligibleSku) {
      if (!row.shippedCount) continue
      const variantId = row.variantId || ''
      const skuCode = variantId ? skuCodeByVariantId.get(variantId) || '' : ''
      const records = skuCode ? companyRecordsByCode.get(skuCode) || [] : []
      const override = skuCode ? companyOverridesByCode.get(skuCode) : undefined
      const parameters = pinduoduoCostParametersOn(profile, records, override, row.date)
      if (!parameters.storeCost) missingStoreCostDates.add(row.date)
      if (!variantId || !skuCode || parameters.productCost.productCost === null) {
        missingProductCostVariantIds.add(variantId || `unmapped:${row.productId}:${row.skuInformation}`)
        continue
      }
      expectedDamageCost += parameters.productCost.productCost
        * policy.secondReturnDamageRate / 100
        * row.shippedCount
      coveredDamageItemCount += row.shippedCount
    }
  }

  const totalLoss = roundTripFreightCost + packagingLoss + expectedDamageCost
  return {
    secondReturnOrderCount,
    secondReturnItemCount,
    coveredOrderCount,
    coveredDamageItemCount,
    roundTripFreightCost,
    packagingLoss,
    expectedDamageCost,
    totalLoss,
    complete: Boolean(policy)
      && coveredOrderCount === secondReturnOrderCount
      && coveredDamageItemCount === secondReturnItemCount,
    damageRate: policy?.secondReturnDamageRate ?? null,
    freightMultiplier: policy?.secondReturnFreightMultiplier ?? null,
    missingStoreCostDates: [...missingStoreCostDates].sort(),
    missingProductCostVariantIds: [...missingProductCostVariantIds].sort(),
  }
}
